import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import LoginModal from '../auth/LoginModal';
import SignupModal from '../auth/SignupModal';
import { Box, Button, Avatar, Typography, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import newAppLogo from '../../assets/images/FERS-Navigator-App-Header.png';

const Header = ({ variant = 'app' }) => {
  const { user, setUser } = useContext(UserContext);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if user has used the app before
  useEffect(() => {
    const hasUsedBefore = localStorage.getItem('fers_calculator_used');
    if (hasUsedBefore && !user) {
      // Show subtle prompt after 30 seconds
      setTimeout(() => {
        setShowLoginPrompt(true);
      }, 30000);
    }
  }, [user]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      handleProfileMenuClose();
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const showLoginModal = () => {
    setLoginModalOpen(true);
    setSignupModalOpen(false);
  };

  const showSignupModal = () => {
    setSignupModalOpen(true);
    setLoginModalOpen(false);
  };

  const closeModals = () => {
    setLoginModalOpen(false);
    setSignupModalOpen(false);
  };

  // When user clicks Get Started, mark that they've used the app
  const handleGetStarted = () => {
    localStorage.setItem('fers_calculator_used', 'true');
  };

  // Landing Page Header
  if (variant === 'landing') {
    return (
      <Box component="header" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderBottom: '1px solid #eaeaea'
      }}>
        <Box className="logo">
          <img src="/fers-navigator-logo.png" alt="FERS Navigator" style={{ height: '32px' }} />
        </Box>
        
        {isMobile ? (
          <Box>
            <IconButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <MenuIcon />
            </IconButton>
            {mobileMenuOpen && (
              <Box sx={{ 
                position: 'absolute', 
                right: 0, 
                top: '64px', 
                backgroundColor: 'white',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: '4px',
                padding: '8px',
                zIndex: 1000
              }}>
                {/* Features, Pricing, and Login buttons hidden until implementation is complete */}
                <MenuItem 
                  onClick={() => {
                    handleGetStarted();
                    window.location.href = '/app';
                  }}
                  sx={{ 
                    color: 'white', 
                    backgroundColor: 'primary.main',
                    '&:hover': { backgroundColor: 'primary.dark' } 
                  }}
                >
                  Get Started
                </MenuItem>
              </Box>
            )}
          </Box>
        ) : (
          <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Features and Pricing links hidden until implementation is complete */}
            <Box className="auth-buttons">
              <Button 
                variant="contained" 
                onClick={() => {
                  handleGetStarted();
                  window.location.href = '/app';
                }}
                sx={{ 
                  backgroundColor: '#3B82F6',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#2563EB', boxShadow: 'none' },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Login/Signup Modals */}
        {loginModalOpen && (
          <LoginModal 
            onClose={closeModals} 
            onSwitchToSignup={showSignupModal} 
          />
        )}
        
        {signupModalOpen && (
          <SignupModal 
            onClose={closeModals} 
            onSwitchToLogin={showLoginModal} 
          />
        )}
      </Box>
    );
  }

  // In-App Header
  return (
    <Box component="header" sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: '#F9FAFB',
      borderBottom: '1px solid #eaeaea'
    }}>
      <Box className="logo">
        <Link to="/">
          <img src={newAppLogo} alt="FERS Navigator" style={{ height: '96px' }} />
        </Link>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {user && !user.isGuest ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography variant="body2" sx={{ color: '#6B7280', display: { xs: 'none', sm: 'block' } }}>
              {user.email || user.displayName}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen} size="small">
              {user.photoURL ? (
                <Avatar 
                  src={user.photoURL} 
                  alt={user.displayName || 'User'} 
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {user.displayName ? user.displayName[0].toUpperCase() : 'U'}
                </Avatar>
              )}
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                  mt: 1.5,
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleSignOut}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        ) : user && user.isGuest ? (
          // Guest user
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography variant="body2" sx={{ 
              color: '#6B7280', 
              fontStyle: 'italic',
              display: { xs: 'none', sm: 'block' }
            }}>
              Guest Mode
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              color="primary" 
              onClick={showLoginModal}
              sx={{ 
                animation: showLoginPrompt ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.4)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(59, 130, 246, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)' }
                }
              }}
            >
              {showLoginPrompt ? 'Save Your Work' : 'Log In'}
            </Button>
          </Box>
        ) : (
          // Not logged in
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button 
              variant="text" 
              color="inherit" 
              onClick={showLoginModal}
              sx={{ color: '#6B7280' }}
            >
              Log In
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              color="primary" 
              onClick={showSignupModal}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Login/Signup Modals */}
      {loginModalOpen && (
        <LoginModal 
          onClose={closeModals} 
          onSwitchToSignup={showSignupModal} 
        />
      )}
      
      {signupModalOpen && (
        <SignupModal 
          onClose={closeModals} 
          onSwitchToLogin={showLoginModal} 
        />
      )}
    </Box>
  );
};

export default Header;
